import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, socket } from './api';

/**
 * Notifications hook (MySQL-backed in-app notifications).
 *
 * Server endpoints:
 *  - GET  /api/notifications?unread=0|1&limit=&offset=
 *  - GET  /api/notifications/unread-count
 *  - POST /api/notifications/:id/read
 *  - POST /api/notifications/:id/unread
 *  - POST /api/notifications/mark-all-read
 *
 * Returns both `list` and `items` as aliases.
 */

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === 'object') return v;
  if (typeof v !== 'string') return v;
  try {
    return JSON.parse(v);
  } catch {
    return v;
  }
}

function normalizeNotif(n) {
  if (!n || typeof n !== 'object') return n;
  return {
    ...n,
    id: n.id != null ? Number(n.id) : n.id,
    data: parseMaybeJson(n.data),
  };
}

function useNotificationsImpl({ limit = 50, unreadOnly = false, auto = true } = {}) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [error, setError] = useState('');

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams();
      qs.set('unread', unreadOnly ? '1' : '0');
      qs.set('limit', String(Math.min(Math.max(Number(limit) || 50, 1), 200)));
      qs.set('offset', '0');

      const [rList, rCount] = await Promise.all([
        apiFetch(`/api/notifications?${qs.toString()}`),
        apiFetch('/api/notifications/unread-count'),
      ]);

      const dataList = await rList.json().catch(() => null);
      const dataCount = await rCount.json().catch(() => null);

      if (!rList.ok) {
        const msg = (dataList && (dataList.error || dataList.message)) || `HTTP ${rList.status}`;
        throw new Error(msg);
      }

      // Server returns an array; be tolerant of wrappers.
      const arr = Array.isArray(dataList)
        ? dataList
        : Array.isArray(dataList?.notifications)
          ? dataList.notifications
          : Array.isArray(dataList?.rows)
            ? dataList.rows
            : [];

      const norm = arr.map(normalizeNotif);
      const countVal = Number(dataCount?.count ?? dataCount?.c ?? 0) || 0;

      if (!mountedRef.current) return;
      setList(norm);
      setUnread(countVal);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e?.message || 'Failed to load notifications');
      setList([]);
      setUnread(0);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [limit, unreadOnly]);

  const markRead = useCallback(async (id) => {
    const nid = Number(id);
    if (!Number.isFinite(nid) || nid <= 0) return;
    try {
      const r = await apiFetch(`/api/notifications/${nid}/read`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);

      // optimistic local update
      setList((prev) =>
        (prev || []).map((n) => (Number(n?.id) === nid ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
      );
      setUnread((u) => Math.max(0, (Number(u) || 0) - 1));
    } catch (e) {
      setError(e?.message || 'Failed to mark read');
    }
  }, []);

  const markUnread = useCallback(async (id) => {
    const nid = Number(id);
    if (!Number.isFinite(nid) || nid <= 0) return;
    try {
      const r = await apiFetch(`/api/notifications/${nid}/unread`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);

      setList((prev) =>
        (prev || []).map((n) => (Number(n?.id) === nid ? { ...n, read_at: null } : n))
      );
      setUnread((u) => (Number(u) || 0) + 1);
    } catch (e) {
      setError(e?.message || 'Failed to mark unread');
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const r = await apiFetch('/api/notifications/mark-all-read', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);

      // optimistic local update
      setList((prev) => (prev || []).map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (e) {
      setError(e?.message || 'Failed to mark all read');
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  // Realtime: when server emits a notification, update local state + unread badge
  useEffect(() => {
    const EVENT = 'notification';

    const onNotif = (payload) => {
      if (!payload) return;
      const n = normalizeNotif(payload);

      // If we don't have an id, just refresh to stay consistent.
      if (!n?.id) {
        refresh();
        return;
      }

      setList((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        if (arr.some((x) => Number(x?.id) === Number(n.id))) return arr;
        return [n, ...arr].slice(0, 200);
      });

      if (!n.read_at) setUnread((u) => (Number(u) || 0) + 1);

      try {
        window.dispatchEvent(new CustomEvent('chit:notification', { detail: n }));
      } catch {}
    };

    socket?.on?.(EVENT, onNotif);
    return () => socket?.off?.(EVENT, onNotif);
  }, [refresh]);

  const items = list;

  return {
    loading,
    error,
    list,
    items,
    unread,
    refresh,
    reload: refresh, // backward compat
    markRead,
    markUnread,
    markAllRead,
  };
}

export default useNotificationsImpl;
export const useNotifications = useNotificationsImpl;
